import { useState, useEffect, useCallback } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import {
  IconUsers,
  IconSearch,
  IconChevronLeft,
  IconChevronRight,
  IconPencil,
  IconTrash,
  IconBell,
  IconBellOff,
} from '@tabler/icons-react';
import UserModal from '../components/UserModal';
import type { User } from '../types';
import { listUsers, deleteUserAdmin, getUserNotificationSettings } from '../services/usersService';
import type { NotificationSettings } from '../types';

interface UserWithSettings {
  user: User;
  notifEnabled: boolean | null; // null = no settings doc
  hasFcmToken: boolean;
}

export default function UsersPage() {
  const [rows, setRows] = useState<UserWithSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageHistory, setPageHistory] = useState<(QueryDocumentSnapshot<DocumentData> | null)[]>(
    []
  );
  const [currentPageCursor, setCurrentPageCursor] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadUsers = useCallback(
    async (cursor: QueryDocumentSnapshot<DocumentData> | null = null) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listUsers(cursor, debouncedSearch);
        // Enrich each user with notification settings summary
        const enriched = await Promise.all(
          result.items.map(async (user): Promise<UserWithSettings> => {
            try {
              const settings: NotificationSettings | null = await getUserNotificationSettings(
                user.id
              );
              return {
                user,
                notifEnabled: settings ? settings.enabled : null,
                hasFcmToken: settings ? !!settings.fcmToken : false,
              };
            } catch {
              return { user, notifEnabled: null, hasFcmToken: false };
            }
          })
        );
        setRows(enriched);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch {
        setError('Failed to load users.');
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch]
  );

  useEffect(() => {
    setPageHistory([]);
    setLastDoc(null);
    setCurrentPageCursor(null);
    loadUsers(null);
  }, [debouncedSearch, loadUsers]);

  function handleNextPage() {
    setPageHistory(h => [...h, currentPageCursor]);
    setCurrentPageCursor(lastDoc);
    loadUsers(lastDoc);
  }

  function handlePreviousPage() {
    const prev = pageHistory[pageHistory.length - 1] ?? null;
    setPageHistory(h => h.slice(0, -1));
    setCurrentPageCursor(prev);
    loadUsers(prev);
  }

  async function handleDelete(user: User) {
    if (
      !window.confirm(
        `Delete user "${user.name || user.email}"? This will permanently remove all their data.`
      )
    )
      return;
    setDeletingId(user.id);
    try {
      await deleteUserAdmin(user.id);
      setRows(r => r.filter(row => row.user.id !== user.id));
    } catch {
      setError(`Failed to delete user ${user.email}.`);
    } finally {
      setDeletingId(null);
    }
  }

  function handleEditSaved() {
    setEditingUser(null);
    loadUsers(pageHistory.length > 0 ? pageHistory[pageHistory.length - 1] : null);
  }

  const currentPage = pageHistory.length + 1;

  return (
    <>
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">Users</div>
            <h2 className="page-title">Users</h2>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label">Search by email</label>
              <div className="input-group">
                <span className="input-group-text">
                  <IconSearch size={16} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="user@example.com"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="card mt-3">
        {loading ? (
          <div className="card-body">
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="card-body">
            <div className="empty">
              <div className="empty-icon">
                <IconUsers size={48} stroke={1.5} />
              </div>
              <p className="empty-title">No users found</p>
              <p className="empty-subtitle text-muted">
                {debouncedSearch ? 'Try a different search.' : 'No users registered yet.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Admin</th>
                    <th>Notifications</th>
                    <th>FCM Token</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ user, notifEnabled, hasFcmToken }) => (
                    <tr key={user.id}>
                      <td>{user.name || <span className="text-muted">—</span>}</td>
                      <td className="text-muted">{user.email}</td>
                      <td>
                        {user.isAdmin ? (
                          <span className="badge bg-red text-white">Admin</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {notifEnabled === null ? (
                          <span className="badge bg-secondary text-white">No settings</span>
                        ) : notifEnabled ? (
                          <span className="badge bg-success text-white">
                            <IconBell size={12} className="me-1" />
                            Enabled
                          </span>
                        ) : (
                          <span className="badge bg-secondary text-white">
                            <IconBellOff size={12} className="me-1" />
                            Disabled
                          </span>
                        )}
                      </td>
                      <td>
                        {hasFcmToken ? (
                          <span className="badge bg-success text-white">Present</span>
                        ) : (
                          <span className="badge bg-secondary text-white">Absent</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-list flex-nowrap">
                          <button
                            className="btn btn-icon"
                            title="Edit user"
                            onClick={() => setEditingUser(user)}
                          >
                            <IconPencil className="icon" />
                          </button>
                          <button
                            className="btn btn-icon text-danger"
                            title="Delete user"
                            disabled={deletingId === user.id}
                            onClick={() => handleDelete(user)}
                          >
                            {deletingId === user.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <IconTrash className="icon" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card-footer d-flex align-items-center">
              <p className="m-0 text-muted">Page {currentPage}</p>
              <ul className="pagination m-0 ms-auto">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <IconChevronLeft size={16} />
                    prev
                  </button>
                </li>
                <li className={`page-item ${!hasMore ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNextPage} disabled={!hasMore}>
                    next
                    <IconChevronRight size={16} />
                  </button>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <UserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={handleEditSaved}
        />
      )}
    </>
  );
}
